import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { useTheme } from "@mui/material";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import { CarriersStore } from "../../../stores/CarriersStore";

const SEARCH_DEBOUNCE_MS = 400;

export type CarriersSearchDropdownProps = {
  title?: string;
  value?: FilterValue[];
  handleSelect: (selectedItems: FilterValue[]) => void;
  disabled?: boolean;
  borderColor?: string;
  fontSize?: string;
  inputHeight?: string;
  borderRadius?: string;
  enabled?: boolean;
};

const CarriersSearchDropdown: React.FC<CarriersSearchDropdownProps> = ({
  title = "Select Delivery Partner",
  value,
  handleSelect,
  disabled = false,
  borderColor,
  fontSize,
  inputHeight,
  borderRadius,
  enabled = true,
}) => {
  const theme = useTheme();
  const carriersStore = useMemo(() => new CarriersStore(), []);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const loadCarriers = async () => {
      try {
        await carriersStore.listCarriers({
          search: debouncedSearch.trim() || undefined,
        });
      } catch {
        // Store surfaces the error inline on the dropdown.
      }
    };

    loadCarriers();
  }, [carriersStore, debouncedSearch, enabled]);

  return (
    <>
      <AppSearchDropdownMulti
        isMultiSelect={false}
        title={title}
        local={false}
        list={carriersStore.toDropdownList()}
        value={value}
        handleSearch={(text) => setSearchText(text ?? "")}
        handleSelect={handleSelect}
        disabled={disabled}
        borderColor={borderColor}
        fontSize={fontSize}
        inputHeight={inputHeight}
        borderRadius={borderRadius}
      />
      {carriersStore.error ? (
        <CustomTypography
          variant="caption"
          color={theme.palette.customColors.borderErrorRedColor}
          sx={{ mt: 0.5 }}
        >
          {carriersStore.error}
        </CustomTypography>
      ) : null}
    </>
  );
};

export default observer(CarriersSearchDropdown);
